import { generateClientId, generateClientSecret } from "../../utils/credentials";

// The one list of integration providers, and every rule about their forms:
// what a new connection posts, how an existing one's config seeds the edit
// form, what an edit posts back, and when it may be saved.
//
// These rules were lifted unchanged from the two screens they replaced
// (IntegrationManageModal for create, IntegrationConfigPage for edit; both
// deleted with the drawer redesign), so the
// payload keys the backend reads are exactly what they were. Config keys are
// camelCase (accountSid, apiKey, passId…) because that is how they are stored
// in integrations.config and read back by the backend services; the top-level
// keys (integration_type, send_customer_enabled…) are snake_case strong params.
// Do not "fix" either side.

// `type` is the Integration enum value the API takes as integration_type;
// `key` is the enum name it returns. `tags` go out with the create payload and
// drive the category filter (Marketing links to /integrations?tag=email|sms).
export const PROVIDERS = [
  {
    key: "shopify",
    type: 7,
    name: "Shopify",
    tags: ["e-commerce"],
    docsUrl: "https://docs.loyaltify.io/en/articles/11460064-shopify",
  },
  { key: "webhook", type: 12, name: "Webhook", tags: ["automation"] },
  { key: "bookingkit", type: 13, name: "BookingKit", tags: ["booking", "e-commerce"] },
  {
    key: "twilio",
    type: 1,
    name: "Twilio",
    tags: ["sms"],
    docsUrl: "https://docs.loyaltify.io/en/articles/11460052-twilio",
  },
  {
    key: "sendgrid",
    type: 2,
    name: "SendGrid",
    tags: ["email"],
    docsUrl: "https://docs.loyaltify.io/en/articles/11460054-sendgrid",
  },
  {
    key: "mailgun",
    type: 4,
    name: "Mailgun",
    tags: ["email"],
    docsUrl: "https://docs.loyaltify.io/en/articles/11460057-mailgun",
  },
  {
    key: "mandrill",
    type: 11,
    name: "Mandrill",
    tags: ["email"],
    docsUrl: "https://docs.loyaltify.io/en/articles/11460058-mandrill",
  },
  {
    key: "mailchimp",
    type: 3,
    name: "Mailchimp",
    tags: ["crm"],
    docsUrl: "https://docs.loyaltify.io/en/articles/11460055-mailchimp",
  },
  {
    key: "brevo",
    type: 10,
    name: "Brevo",
    tags: ["crm"],
    docsUrl: "https://docs.loyaltify.io/en/articles/11460059-brevo",
  },
  // Listed so merchants know it is coming; it can never be connected.
  { key: "stripe", type: 5, name: "Stripe", tags: ["payment"], soon: true },
];

export const providerByKey = (key) => PROVIDERS.find((p) => p.key === key);

// Tag value (as sent to the API and used in ?tag=) -> i18n key suffix.
export const TAG_LABEL_KEYS = {
  "e-commerce": "ecommerce",
  booking: "booking",
  email: "email",
  sms: "sms",
  crm: "crm",
  automation: "automation",
  payment: "payment",
};

export const ALL_TAGS = PROVIDERS.reduce((tags, p) => {
  p.tags.forEach((tag) => {
    if (!tags.includes(tag)) tags.push(tag);
  });
  return tags;
}, []);

export const MAILGUN_REGIONS = [
  { value: "api.mailgun.net", labelKey: "integrations.regions.global" },
  { value: "api.eu.mailgun.net", labelKey: "integrations.regions.eu" },
];

// Providers whose only credential is an API key.
const API_KEY_ONLY = ["sendgrid", "mailchimp", "mandrill", "brevo"];
const CUSTOMER_SYNC = ["mailchimp", "brevo"];
export const hasCustomerSync = (key) => CUSTOMER_SYNC.includes(key);

// Credential fields shown for both create and edit. `secret` renders a
// show/hide toggle; `docs` puts the provider's how-to link under the field.
export const CREDENTIAL_FIELDS = {
  twilio: [
    {
      name: "accountSid",
      labelKey: "integrations.fields.accountSid",
      placeholder: "ACxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx",
    },
    { name: "authToken", labelKey: "integrations.fields.authToken", secret: true, docs: true },
  ],
  sendgrid: [{ name: "apiKey", labelKey: "integrations.fields.apiKey", secret: true, docs: true }],
  mailchimp: [{ name: "apiKey", labelKey: "integrations.fields.apiKey", secret: true, docs: true }],
  mandrill: [{ name: "apiKey", labelKey: "integrations.fields.apiKey", secret: true, docs: true }],
  brevo: [{ name: "apiKey", labelKey: "integrations.fields.apiKey", secret: true, docs: true }],
  mailgun: [
    { name: "apiUrl", labelKey: "integrations.fields.region", select: MAILGUN_REGIONS },
    { name: "apiKey", labelKey: "integrations.fields.apiKey", secret: true, docs: true },
  ],
  shopify: [
    {
      name: "shopDomain",
      labelKey: "integrations.fields.shopDomain",
      placeholder: "your-store.myshopify.com",
      hintKey: "integrations.fields.shopDomainHint",
    },
  ],
  webhook: [
    {
      name: "webhookUrl",
      labelKey: "integrations.webhook.endpointUrl",
      placeholder: "https://example.com/webhook",
      // text, not url: the old form accepted any non-empty value.
      inputMode: "url",
    },
  ],
  bookingkit: [],
  stripe: [],
};

const BLANK_VALUES = {
  accountSid: "",
  authToken: "",
  apiKey: "",
  apiUrl: "",
  shopDomain: "",
  webhookSecret: "",
  passId: "",
  enableCustomerIntegration: false,
  selectedAudience: "",
  sendCustomerEnabled: false,
  webhookUrl: "",
  webhookEvents: [],
  webhookAuthType: "none",
  webhookAuthToken: "",
  bkEnabled: true,
  bkOauthClientId: "",
  bkOauthClientSecret: "",
  bkAllowedVendorIds: [],
  bkAllowedProductIds: [],
  bkEligibleStatuses: ["ACCEPTED"],
  bkExpectedCurrency: "EUR",
  bkClaimGraceDays: 7,
};

export const createInitialValues = () => ({ ...BLANK_VALUES });

// --- Create -----------------------------------------------------------------

export function buildCreatePayload(key, values) {
  const provider = providerByKey(key);
  const payload = {
    integration_type: provider?.type,
    config: {},
    tags: provider?.tags || [],
  };

  switch (key) {
    case "twilio":
      payload.config = { accountSid: values.accountSid, authToken: values.authToken };
      break;
    case "mailgun":
      payload.config = { apiKey: values.apiKey, apiUrl: values.apiUrl };
      break;
    case "shopify":
      payload.config = { shopDomain: values.shopDomain };
      break;
    case "webhook":
      payload.config = webhookConfig(values);
      break;
    case "bookingkit":
      // Connected with empty filters; the merchant fills them in from Configure.
      payload.config = {
        enabled: true,
        oauthClientId: generateClientId(),
        oauthClientSecret: generateClientSecret(),
        allowedVendorIds: [],
        allowedProductIds: [],
        eligibleStatuses: ["ACCEPTED"],
        expectedCurrency: "EUR",
        claimGracePeriodDays: 7,
      };
      break;
    default:
      if (API_KEY_ONLY.includes(key)) payload.config = { apiKey: values.apiKey };
  }

  return payload;
}

// The only rule the connect form enforced beyond its HTML `required` fields.
export const isCreateSubmitBlocked = (key, values) =>
  key === "webhook" && values.webhookEvents.length === 0;

// --- Edit -------------------------------------------------------------------

const gracePeriod = (days) => (Number.isFinite(days) && days > 0 ? days : 7);

// Seeds the edit form from a saved integration. `original` is what "has the
// form changed" compares against; it differs from `values` in one place on
// purpose: BookingKit credentials missing from a legacy config are generated
// here, and an empty original makes them count as a change so Save persists
// them.
export function seedEditState(integration) {
  const key = integration?.integration_type;
  const config = integration?.config || {};
  const values = createInitialValues();
  const original = createInitialValues();
  const set = (patch, originalPatch = patch) => {
    Object.assign(values, patch);
    Object.assign(original, originalPatch);
  };

  if (key === "twilio") {
    set({ accountSid: config.accountSid || "", authToken: config.authToken || "" });
  } else if (API_KEY_ONLY.includes(key)) {
    set({ apiKey: config.apiKey || "" });
    if (hasCustomerSync(key)) {
      if (config.listId) set({ selectedAudience: config.listId });
      set({ sendCustomerEnabled: integration.send_customer_enabled || false });
    }
  } else if (key === "mailgun") {
    set({ apiKey: config.apiKey || "", apiUrl: config.apiUrl || "" });
  } else if (key === "shopify") {
    set({
      shopDomain: config.shopDomain || "",
      webhookSecret: config.webhookSecret || "",
      passId: config.passId || "",
      enableCustomerIntegration: integration.customer_integration_enabled || false,
    });
  } else if (key === "bookingkit") {
    const shared = {
      passId: config.passId || "",
      bkEnabled: config.enabled !== false,
      bkAllowedVendorIds: config.allowedVendorIds || [],
      bkAllowedProductIds: config.allowedProductIds || [],
      bkEligibleStatuses: config.eligibleStatuses || ["ACCEPTED"],
      bkExpectedCurrency: config.expectedCurrency || "EUR",
      bkClaimGraceDays: gracePeriod(config.claimGracePeriodDays),
    };
    set(
      {
        ...shared,
        bkOauthClientId: config.oauthClientId || generateClientId(),
        bkOauthClientSecret: config.oauthClientSecret || generateClientSecret(),
      },
      {
        ...shared,
        bkOauthClientId: config.oauthClientId || "",
        bkOauthClientSecret: config.oauthClientSecret || "",
      },
    );
  } else if (key === "webhook") {
    set({
      webhookUrl: config.url || "",
      webhookEvents: config.events || [],
      webhookAuthType: config.auth_type || "none",
      webhookAuthToken: config.auth_token || "",
    });
  }

  return { values, original };
}

function webhookConfig(values) {
  return {
    url: values.webhookUrl,
    events: values.webhookEvents,
    auth_type: values.webhookAuthType,
    ...(values.webhookAuthType === "bearer" && { auth_token: values.webhookAuthToken }),
  };
}

export function buildEditPayload(key, values, integration) {
  const provider = providerByKey(key);
  const payload = {
    integration_type: provider?.type,
    config: {},
    tags: integration?.tags || [], // keep the saved tags
  };
  const saved = integration?.config || {};

  switch (key) {
    case "twilio":
      payload.config = { accountSid: values.accountSid, authToken: values.authToken };
      break;
    case "sendgrid":
    case "mandrill":
      payload.config = { apiKey: values.apiKey };
      break;
    case "mailchimp":
    case "brevo":
      payload.config = { apiKey: values.apiKey, listId: values.selectedAudience };
      payload.send_customer_enabled = values.sendCustomerEnabled;
      break;
    case "mailgun":
      payload.config = { apiKey: values.apiKey, apiUrl: values.apiUrl };
      break;
    case "shopify":
      payload.config = {
        ...saved, // keeps the admin token and anything else not on this form
        shopDomain: values.shopDomain,
        webhookSecret: values.webhookSecret,
        passId: values.passId,
      };
      payload.customer_integration_enabled = values.enableCustomerIntegration;
      break;
    case "webhook":
      payload.config = webhookConfig(values);
      break;
    case "bookingkit":
      payload.config = {
        ...saved,
        enabled: values.bkEnabled,
        passId: values.passId,
        oauthClientId: values.bkOauthClientId,
        oauthClientSecret: values.bkOauthClientSecret,
        allowedVendorIds: values.bkAllowedVendorIds,
        allowedProductIds: values.bkAllowedProductIds,
        eligibleStatuses: values.bkEligibleStatuses,
        expectedCurrency: values.bkExpectedCurrency,
        claimGracePeriodDays: Number(values.bkClaimGraceDays) || 7,
      };
      break;
    default:
      break;
  }

  return payload;
}

const filled = (v) => v !== null && v !== undefined && String(v).trim() !== "";

// Same rules as the old edit page's isFormValid. String() only guards a
// numeric passId, which used to throw on .trim().
export function isEditFormValid(key, values) {
  if (key === "mailchimp" && values.sendCustomerEnabled) {
    return filled(values.selectedAudience);
  }
  if (key === "brevo" && values.sendCustomerEnabled) return true;
  if (key === "shopify") {
    return (
      filled(values.webhookSecret) &&
      (!values.enableCustomerIntegration || filled(values.passId))
    );
  }
  if (key === "webhook") {
    return filled(values.webhookUrl) && values.webhookEvents.length > 0;
  }
  if (key === "bookingkit") {
    return (
      filled(values.passId) &&
      filled(values.bkOauthClientId) &&
      filled(values.bkOauthClientSecret) &&
      values.bkAllowedVendorIds.length > 0 &&
      values.bkEligibleStatuses.length > 0
    );
  }
  return true;
}

const sameSet = (a, b) =>
  JSON.stringify((a || []).slice().sort()) === JSON.stringify((b || []).slice().sort());

// Save stays disabled until something the payload carries has changed.
export function hasEditFormChanged(key, values, original) {
  const differs = (...names) => names.some((n) => values[n] !== original[n]);
  switch (key) {
    case "twilio":
      return differs("accountSid", "authToken");
    case "mailchimp":
    case "brevo":
      return differs("apiKey", "selectedAudience", "sendCustomerEnabled");
    case "mailgun":
      return differs("apiKey", "apiUrl");
    case "shopify":
      return differs("shopDomain", "webhookSecret", "passId", "enableCustomerIntegration");
    case "webhook":
      return (
        differs("webhookUrl", "webhookAuthType", "webhookAuthToken") ||
        !sameSet(values.webhookEvents, original.webhookEvents)
      );
    case "bookingkit":
      return (
        differs(
          "passId",
          "bkOauthClientId",
          "bkOauthClientSecret",
          "bkEnabled",
          "bkExpectedCurrency",
        ) ||
        Number(values.bkClaimGraceDays) !== Number(original.bkClaimGraceDays) ||
        !sameSet(values.bkAllowedVendorIds, original.bkAllowedVendorIds) ||
        !sameSet(values.bkAllowedProductIds, original.bkAllowedProductIds) ||
        !sameSet(values.bkEligibleStatuses, original.bkEligibleStatuses)
      );
    default:
      return differs("apiKey");
  }
}

// --- Display helpers --------------------------------------------------------

export function urlHost(url) {
  try {
    return new URL(url).host || url;
  } catch {
    return url || "";
  }
}

const apiBase = () =>
  (import.meta.env.VITE_API_URL || "").trim().replace(/\/+$/, "");
export const bookingkitTokenUrl = () => `${apiBase()}/webhooks/bookingkit/token`;
export const bookingkitWebhookUrl = () => `${apiBase()}/webhooks/bookingkit`;

// One line under a connected card's name. Falls back to the provider
// description when the list response carries no config (it is redacted for
// non-admins, who cannot reach this page anyway).
export function connectedSummary(integration, t) {
  const key = integration?.integration_type;
  const config = integration?.config || {};
  switch (key) {
    case "shopify":
      return config.shopDomain || t("integrations.description.shopify");
    case "webhook": {
      const count = (config.events || []).length;
      if (!config.url) return t("integrations.description.webhook");
      return t("integrations.summary.webhook", { count, host: urlHost(config.url) });
    }
    case "bookingkit":
      if (config.enabled === false) return t("integrations.summary.bookingkitOff");
      if (!(config.allowedVendorIds || []).length) {
        return t("integrations.summary.bookingkitNoVendors");
      }
      return t("integrations.summary.bookingkitOn");
    case "mailchimp":
    case "brevo":
      return integration.send_customer_enabled
        ? t("integrations.summary.syncOn")
        : t("integrations.summary.syncOff");
    case "twilio":
      return t("integrations.summary.sms");
    case "mailgun": {
      const region = MAILGUN_REGIONS.find((r) => r.value === config.apiUrl);
      return region
        ? t("integrations.summary.emailRegion", { region: t(region.labelKey) })
        : t("integrations.summary.email");
    }
    case "sendgrid":
    case "mandrill":
      return t("integrations.summary.email");
    default:
      return "";
  }
}

// What disconnecting removes, per provider (Integration#destroy on the API:
// webhook_events and bookingkit_orders are dependent: :destroy; pending and
// failed messages using it are destroyed, sent ones are kept). The last line
// applies to every provider.
export function disconnectLines(integration, t) {
  const key = integration?.integration_type;
  const name = providerByKey(key)?.name || "";
  const config = integration?.config || {};
  const lines = [];
  switch (key) {
    case "shopify":
      lines.push(t("integrations.disconnectDialog.shopify"));
      break;
    case "webhook":
      lines.push(
        config.url
          ? t("integrations.disconnectDialog.webhookStops", { host: urlHost(config.url) })
          : t("integrations.disconnectDialog.webhookStopsGeneric"),
        t("integrations.disconnectDialog.webhookHistory"),
      );
      break;
    case "bookingkit":
      lines.push(
        t("integrations.disconnectDialog.bookingkitStops"),
        t("integrations.disconnectDialog.bookingkitPending"),
      );
      break;
    case "twilio":
      lines.push(
        t("integrations.disconnectDialog.sms"),
        t("integrations.disconnectDialog.messages"),
      );
      break;
    case "sendgrid":
    case "mailgun":
    case "mandrill":
      lines.push(
        t("integrations.disconnectDialog.email"),
        t("integrations.disconnectDialog.messages"),
      );
      break;
    case "mailchimp":
    case "brevo":
      lines.push(t("integrations.disconnectDialog.crm", { name }));
      break;
    default:
      break;
  }
  lines.push(t("integrations.disconnectDialog.settings"));
  return lines;
}
